import dayjs from 'dayjs';

import type {
  BNClientConferencesCreateParams,
  BNCoachConferencesCreateParams,
} from '../../../api/types/bnrks/methods';
import type {
  CoreClientConferencesCreateParams,
  CoreCoachConferencesCreateParams,
} from '../../../api/types/core/methods';
import type {Combine} from '../../../utils/utility-types';
import type {BaseConfig} from '../utils';

import {conferenceExpandCommon, conferenceStore} from '../../../api/conference';
import {apiRequest} from '../../../api/utils/apiRequest';
import {ISOStringToDatetimeObj} from '../../../utils/datetime';
import {AppError} from '../../../utils/errors/app';
import {getUuid} from '../../../utils/getUuid';
import {updateProgramScheduleHook} from '../program/updateProgramScheduleHook';

export {conferenceCreate};

type CoachConfig = BaseConfig<
  Omit<CreateParams, 'end_date' | 'start_date' | 'uuid'>
> & {
  recurring: {
    duration: number;
    endDate: Date;
    interval?: number;
    startDate: Date;
  };
};

type CreateParams = Combine<
  CoreCoachConferencesCreateParams,
  BNCoachConferencesCreateParams
>;

async function conferenceCreate() {
  throw new AppError(
    'Generic method is not defined. Call role specific method instead.',
  );
}

conferenceCreate.asClient = asClient;
conferenceCreate.asCoach = asCoach;

async function asClient({
  params,
}: BaseConfig<
  Combine<CoreClientConferencesCreateParams, BNClientConferencesCreateParams>
>) {
  const createResult = await conferenceStore.client.create(params);

  return createResult;
}

async function asCoach({params, recurring}: CoachConfig) {
  try {
    const {duration, endDate, interval, startDate} = recurring;

    const dates = [startDate];

    const createParams: CreateParams[] = [];

    if (interval) {
      let date = startDate;

      while (dayjs(date).add(interval, 'day') <= dayjs(endDate)) {
        date = dayjs(date).add(interval, 'day').toDate();

        dates.push(date);
      }
    }

    const uuid = getUuid();

    dates.forEach(date => {
      createParams.push({
        ...params,
        end_date: ISOStringToDatetimeObj(
          dayjs(date).add(duration, 'second').toISOString(),
        ),
        expand: params.expand || conferenceExpandCommon,
        start_date: ISOStringToDatetimeObj(dayjs(date).toISOString()),
        uuid,
      });
    });

    const result = await conferenceStore.coach.createBatch(createParams);

    updateProgramScheduleHook(result, 'create');

    return result;
  } catch (e) {
    let errorToThrow = e;

    if (Array.isArray(e)) {
      errorToThrow = e.find(item => apiRequest.isRpcRequestError(item)) || e;
    }

    throw errorToThrow;
  }
}
